/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-03-18 14:20:22
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import moment from 'moment';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import { InlineError, TextStyle, Icon } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Select, { components } from 'react-select';
import { ClockMajor } from '@shopify/polaris-icons';

import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';
import { selectStyle } from 'src/components/views/common/forms/render-select.style';
import { getStoreDate, getStoreTime } from 'src/helpers/date-time-helper';

import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { DATE_FORMAT } from 'src/constants/language-setting';
import { TIME_DURATION_OPTIONS } from 'src/constants/timer';
import { generateTimeOptions } from 'src/helpers/time-helper';

type Props = WithI18nProps;

type State = { timeOptions: Array<Object>, cacheUniqs: number };

const Control = ({ children, ...props }) => (
  <components.Control {...props}>
    <div className="Select__Prefix">
      <Icon source={ClockMajor} />
    </div>
    {children}
  </components.Control>
);

class RenderTimeComponent extends Component<Props, State> {
  state = {
    timeOptions: [],
    cacheUniqs: [0]
  };

  componentDidMount() {
    const { appLanguage, selectedDate } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const today = getStoreDate(new Date());

    let isToday = false;
    if (moment(selectedDate, formatDateNumber).isValid()) {
      if (moment(selectedDate, formatDateNumber).diff(moment(today)) === 0) {
        isToday = true;
      }
    }
    const timeOptions = this.buildTimeOptions(isToday);
    this.setState({ timeOptions, cacheUniqs: [1] });
  }

  componentWillReceiveProps(nextProps) {
    const { selectedDate } = nextProps;
    const { appLanguage, selectedDate: prevSelectedDate } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const today = getStoreDate(new Date());

    // Force render component if the options change
    if (selectedDate !== prevSelectedDate && moment(selectedDate, formatDateNumber).isValid()) {
      if (moment(selectedDate, formatDateNumber).diff(moment(today)) === 0) {
        // Select from another date to today
        const timeOptions = this.buildTimeOptions(true);
        this.setState({ timeOptions, cacheUniqs: [this.state.cacheUniqs[0] + 1] });
      } else {
        // Select from today to another date
        const timeOptions = this.buildTimeOptions(false);
        this.setState({ timeOptions, cacheUniqs: [this.state.cacheUniqs[0] + 1] });
      }
    }
  }

  render() {
    const { meta, label, tooltip, input, isDisabled, textHelper, placeholderEmpty, i18n } =
      this.props;
    const { timeOptions } = this.state;
    const hasError = !!meta.error && !!meta.touched;
    let { placeholder } = this.props;
    placeholder = _.isEmpty(timeOptions)
      ? placeholderEmpty || i18n.translate('AdminHTML.Common.Empty.select_Placeholder')
      : placeholder;

    return (
      <div className={`time-select ${hasError ? 'Polaris-TextField--error' : ''}`}>
        <RenderLabelTooltip label={label} tooltip={tooltip} />
        <Select
          options={timeOptions}
          id={input.name}
          value={input.value}
          onChange={value => {
            input.onChange(value);
          }}
          label={label}
          styles={selectStyle}
          placeholder={placeholder}
          maxMenuHeight={150}
          //$FlowFixMe
          menuPortalTarget={document.body}
          classNamePrefix="BC-Select"
          isDisabled={isDisabled ? true : false}
          cacheUniqs={this.state.cacheUniqs}
          noOptionsMessage={() => i18n.translate('AdminHTML.Common.Empty.reactSelect_NoOptions')}
          components={{ Control }}
        />

        {textHelper ? (
          <p style={{ marginTop: '0.25rem' }}>
            <TextStyle variation="subdued">{textHelper}</TextStyle>
          </p>
        ) : (
          ''
        )}
        {meta.error ? <InlineError message={meta.error} /> : ''}
      </div>
    );
  }

  buildTimeOptions = isToday => {
    const { tenantId } = this.props;
    const duration = TIME_DURATION_OPTIONS[tenantId] || TIME_DURATION_OPTIONS['default'];
    const result = generateTimeOptions(duration);
    if (isToday) {
      const currentTime = new Date();
      const { hour, minute } = getStoreTime(currentTime);

      _.remove(result, item => item.value < hour * 60 + minute + 1);
    }
    return result;
  };
}

const mapStateToProps = (state, props) => ({
  appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
  tenantId: _.get(state.oauth.tenantApp, 'tenantId', '')
});

export const RenderTime = withI18n()(connect(mapStateToProps, {})(RenderTimeComponent));
